import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { featureTypeId } from '@/enums';
import type { IMaterialObject, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const FEATURES_ROOT = ['features'] as const;

interface FeaturesListParams {
    projectId: number;
    version?: string;
    type?: featureTypeId;
    tags?: string;
}

interface FeatureDetailParams {
    projectId: number;
    version?: string;
    type?: featureTypeId;
    featureId: number;
}

const fetchers = {
    list: {
        key: ({ projectId, version, type, tags }: FeaturesListParams) =>
            [...FEATURES_ROOT, 'list', { projectId, version, type, tags }] as const,
        fetch: async ({ projectId, version, type, tags }: FeaturesListParams): Promise<IMaterialObject[]> => {
            const qs = new URLSearchParams();
            if (type) qs.set('feature_type_id', String(type));
            if (version) qs.set('version', version);
            if (tags) qs.set('tags', tags);
            const url = `projects/${projectId}/features${qs.toString() ? `?${qs.toString()}` : ''}`;
            const response: ResponseType<IMaterialObject[]> = await request(url);
            return response.data;
        },
    },
    detail: {
        key: ({ projectId, version, type, featureId }: FeatureDetailParams) =>
            [...FEATURES_ROOT, 'detail', { projectId, version, type, featureId }] as const,
        fetch: async ({ projectId, version, type, featureId }: FeatureDetailParams): Promise<IMaterialObject> => {
            const qs = new URLSearchParams();
            if (type) qs.set('feature_type_id', String(type));
            if (version) qs.set('version', version);
            const url = `projects/${projectId}/features/${featureId}${qs.toString() ? `?${qs.toString()}` : ''}`;
            const response: ResponseType<IMaterialObject> = await request(url);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const featuresEntity: EntityDefinition<typeof fetchers> = {
    name: 'features',
    roots: [FEATURES_ROOT],
    fetchers,
};

export const useFeaturesList = createEntityQuery(featuresEntity, 'list');
export const useFeatureDetail = createEntityQuery(featuresEntity, 'detail');

export type { FeatureDetailParams, FeaturesListParams };
